import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Tooltip } from 'antd';
import { CloseOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { serverFetch } from '@server';
import { showError, fmtDate } from '@extensions/utils';
import TaskPriority from './task-priority';
import './dashboard.css';

// id задач личного «Плана на неделю» и переключатель добавления/удаления.
// allCompanies — по всем фирмам (суперадмин), onChange — после успешного изменения
export const useWeekPlanIds = ({ allCompanies = false, onChange } = {}) => {
    const [ids, setIds] = useState(() => new Set());
    const scope = allCompanies ? 'allCompanies=true' : '';

    const reload = useCallback(() => {
        serverFetch(`weekplan/ids?${scope}`).then((list) => setIds(new Set(list))).catch(showError);
    }, [scope]);

    useEffect(() => { reload(); }, [reload]);

    const toggle = useCallback((taskId) => {
        const inPlan = ids.has(taskId);
        const apply = (add) => setIds((prev) => {
            const next = new Set(prev);
            if (add) next.add(taskId); else next.delete(taskId);
            return next;
        });
        apply(!inPlan);
        serverFetch(`weekplan/${taskId}?${scope}`, { method: inPlan ? 'DELETE' : 'POST' })
            .then(() => onChange?.())
            .catch((e) => { apply(inPlan); showError(e); });
    }, [ids, scope, onChange]);

    return { ids, toggle, reload };
};

// Звёздочка «в план / из плана»
export const WeekPlanStar = ({ active, onClick, size }) => (
    <Tooltip title={active ? 'Убрать из плана на неделю' : 'Добавить в план на неделю'}>
        <Button
            type="text"
            size={size ?? 'small'}
            className={`week-plan-star${active ? ' active' : ''}`}
            icon={active ? <StarFilled /> : <StarOutlined />}
            aria-pressed={active}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
        />
    </Tooltip>
);

// allCompanies — сводный план суперадмина по всем активным фирмам (с подписью фирмы);
// onOpen(task) — открыть задачу (для чужой фирмы родитель переключает фирму);
// onChange — состав плана изменён (убрали задачу), чтобы обновить звёздочки
const WeekPlan = ({ onOpen, reloadKey, allCompanies = false, onChange }) => {
    const [items, setItems] = useState([]);
    const scope = allCompanies ? 'allCompanies=true' : '';

    const load = useCallback(() => {
        serverFetch(`weekplan?${scope}`).then(setItems).catch(showError);
    }, [scope]);

    useEffect(() => { load(); }, [load, reloadKey]);

    const setDone = (task, done) => {
        serverFetch(`weekplan/${task.id}/closed?value=${done}&${scope}`, { method: 'PUT' })
            .then(load)
            .catch(showError);
    };

    const remove = (task) => {
        setItems((prev) => prev.filter((t) => t.id !== task.id));
        serverFetch(`weekplan/${task.id}?${scope}`, { method: 'DELETE' })
            .then(() => onChange?.())
            .catch((e) => { showError(e); load(); });
    };

    const removeClosed = () => {
        serverFetch(`weekplan/closed?${scope}`, { method: 'DELETE' })
            .then(() => { load(); onChange?.(); })
            .catch(showError);
    };

    const doneCount = items.filter((t) => t.isClosed).length;
    const today = dayjs().startOf('day');

    // Группы по фирме (только в сводном режиме), порядок задач внутри — серверный
    const groups = useMemo(() => {
        if (!allCompanies) return [{ key: 'all', items }];
        const map = new Map();
        items.forEach((t) => {
            const key = t.companyId ?? 0;
            if (!map.has(key)) map.set(key, { key, name: t.companyName, items: [] });
            map.get(key).items.push(t);
        });
        return [...map.values()].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'ru'));
    }, [items, allCompanies]);

    const renderRow = (task) => (
        <div key={task.id} className={`week-plan-row${task.isClosed ? ' done' : ''}`}>
            <Checkbox
                checked={task.isClosed}
                onChange={(e) => setDone(task, e.target.checked)}
                aria-label={task.isClosed ? 'Вернуть в работу' : 'Отметить выполненной'}
            />
            <TaskPriority priority={task.priority} />
            <button type="button" className="week-plan-name" onClick={() => onOpen(task)}>
                {task.name || `#${task.id}`}
            </button>
            {task.projectName && (
                <span className="week-plan-project" title={task.projectName}>{task.projectName}</span>
            )}
            <span className={`dashboard-task-meta${!task.isClosed && task.planDate && dayjs(task.planDate).isBefore(today) ? ' overdue' : ''}`}>
                {fmtDate(task.planDate)}
                {task.authorName ? `${task.planDate ? ' · ' : ''}${task.authorName}` : ''}
            </span>
            <Tooltip title="Убрать из плана">
                <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => remove(task)} />
            </Tooltip>
        </div>
    );

    return (
        <section className="dashboard-card week-plan">
            <div className="dashboard-card-head">
                <h3>
                    План на неделю
                    {items.length > 0 && <span className="week-plan-count">выполнено {doneCount} из {items.length}</span>}
                </h3>
                {doneCount > 0 && (
                    <button type="button" className="dashboard-all" onClick={removeClosed}>Убрать выполненные</button>
                )}
            </div>
            {items.length === 0 && (
                <div className="dashboard-empty">
                    Добавьте задачи звёздочкой <StarOutlined /> в карточке задачи или в журнале
                </div>
            )}
            {groups.map((g) => (
                <div key={g.key} className="week-plan-group">
                    {allCompanies && <div className="week-plan-group-name">{g.name || 'Без фирмы'}</div>}
                    {g.items.map(renderRow)}
                </div>
            ))}
        </section>
    );
};

export default WeekPlan;
